import {Component, input, OnInit, output, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormGroup, FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {AppInputComponent} from '@shared/components/forms/app-input/app-input.component';
import {PersonTypeHelper} from '@core/models/person-type.enums';

@Component({
    selector: 'app-step-1-basic-info',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, AppInputComponent],
    template: `
        <div class="step-content">
            <h4 class="mb-1">Informações da Campanha</h4>
            <p class="text-muted mb-4">Dê um nome e, opcionalmente, uma descrição para identificar sua campanha.</p>

            <form [formGroup]="form">
                <div class="row g-3 mb-4">
                    <div class="col-md-6">
                        <app-input
                                formControlName="name"
                                label="Nome da Campanha"
                                placeholder="Ex: PGFN Sul - Janeiro 2026"
                                [required]="true"
                        />
                    </div>
                    <div class="col-md-6">
                        <app-input
                                formControlName="description"
                                label="Descrição (Opcional)"
                                placeholder="Prospecção de empresas do Sul..."
                        />
                    </div>
                </div>
            </form>

            <h5 class="mb-3"><i class="ti ti-filter"></i> Filtros Aplicados</h5>
            <div class="card bg-light-subtle">
                <div class="card-body">
                    @if (hasFilters()) {
                        <!-- Dados da Empresa -->
                        @if (filters().q || filters().state || filters().city || filters().companySize?.length || filters().registrationStatus || filters().personType) {
                            <div class="mb-3">
                                <h6 class="text-muted mb-2"><i class="ti ti-building me-1"></i> Dados da Empresa</h6>
                                <div class="d-flex flex-wrap gap-2">
                                    @if (filters().q) {
                                        <span class="badge fs-6 fw-normal bg-primary-subtle text-primary-emphasis">
                                            <i class="ti ti-list-search me-1"></i>
                                            Busca: {{ filters().q }}
                                        </span>
                                    }
                                    @if (filters().state) {
                                        <span class="badge fs-6 fw-normal bg-primary-subtle text-primary-emphasis">
                                            <i class="ti ti-map-pin me-1"></i>
                                            Estado: {{ filters().state }}
                                        </span>
                                    }
                                    @if (filters().city) {
                                        <span class="badge fs-6 fw-normal bg-primary-subtle text-primary-emphasis">
                                            <i class="ti ti-building me-1"></i>
                                            Cidade: {{ filters().city }}
                                        </span>
                                    }
                                    @if (filters().companySize && filters().companySize.length > 0) {
                                        <span class="badge fs-6 fw-normal bg-secondary-subtle text-secondary-emphasis">
                                            <i class="ti ti-building-community me-1"></i>
                                            Porte: {{ filters().companySize.join(', ') }}
                                        </span>
                                    }
                                    @if (filters().registrationStatus) {
                                        <span class="badge fs-6 fw-normal bg-dark">
                                            <i class="ti ti-file-check me-1"></i>
                                            Situação: {{ filters().registrationStatus }}
                                        </span>
                                    }
                                    @if (filters().personType) {
                                        <span class="badge fs-6 fw-normal bg-primary-subtle text-primary-emphasis">
                                            <i class="ti ti-user me-1"></i>
                                            Tipo: {{ PersonTypeHelper.getFullLabel(filters().personType) }}
                                        </span>
                                    }
                                </div>
                            </div>
                        }
                        
                        <!-- Dados Consolidados -->
                        @if (filters().minEstimatedDebt || filters().maxEstimatedDebt || filters().minTotalInstallmentRemaining || filters().maxTotalInstallmentRemaining) {
                            <div class="mb-3">
                                <h6 class="text-muted mb-2"><i class="ti ti-sum me-1"></i> Dados Consolidados (Total)</h6>
                                <div class="d-flex flex-wrap gap-2">
                                    @if (filters().minEstimatedDebt || filters().maxEstimatedDebt) {
                                        <span class="badge fs-6 fw-normal bg-success-subtle text-success-emphasis">
                                            <i class="ti ti-currency-real me-1"></i>
                                            Saldo Dev. Total: {{ filters().minEstimatedDebt | currency }} - {{ filters().maxEstimatedDebt | currency }}
                                        </span>
                                    }
                                    @if (filters().minTotalInstallmentRemaining || filters().maxTotalInstallmentRemaining) {
                                        <span class="badge fs-6 fw-normal bg-info-subtle text-info-emphasis">
                                            <i class="ti ti-list-numbers me-1"></i>
                                            Parc. Rest. Total: {{ filters().minTotalInstallmentRemaining || 'N/A' }} - {{ filters().maxTotalInstallmentRemaining || 'N/A' }}
                                        </span>
                                    }
                                </div>
                            </div>
                        }
                        
                        <!-- Filtros por Dívida Específica -->
                        @if (filters().minDebtInstallmentValue || filters().maxDebtInstallmentValue || filters().minInstallmentRemaining || filters().maxInstallmentRemaining || filters().startMonthInstallment || filters().endMonthInstallment || filters().negotiationType?.length || filters().excludeRescindedTransacaoRepactuacaoUpTo26Overdues) {
                            <div class="mb-0">
                                <h6 class="text-muted mb-2"><i class="ti ti-file-invoice me-1"></i> Por Dívida Específica</h6>
                                <div class="d-flex flex-wrap gap-2">
                                    @if (filters().minDebtInstallmentValue || filters().maxDebtInstallmentValue) {
                                        <span class="badge fs-6 fw-normal bg-warning-subtle text-warning-emphasis">
                                            <i class="ti ti-currency-real me-1"></i>
                                            Valor Parc.: {{ filters().minDebtInstallmentValue | currency }} - {{ filters().maxDebtInstallmentValue | currency }}
                                        </span>
                                    }
                                    @if (filters().minInstallmentRemaining || filters().maxInstallmentRemaining) {
                                        <span class="badge fs-6 fw-normal bg-warning-subtle text-warning-emphasis">
                                            <i class="ti ti-list-numbers me-1"></i>
                                            Parc. Rest.: {{ filters().minInstallmentRemaining || 'N/A' }} - {{ filters().maxInstallmentRemaining || 'N/A' }}
                                        </span>
                                    }
                                    @if (filters().startMonthInstallment || filters().endMonthInstallment) {
                                        <span class="badge fs-6 fw-normal bg-warning-subtle text-warning-emphasis">
                                            <i class="ti ti-calendar me-1"></i>
                                            Período: {{ filters().startMonthInstallment || 'N/A' }} a {{ filters().endMonthInstallment || 'N/A' }}
                                        </span>
                                    }
                                    @if (filters().negotiationType && filters().negotiationType.length > 0) {
                                        <span class="badge fs-6 fw-normal bg-warning-subtle text-warning-emphasis">
                                            <i class="ti ti-file-text me-1"></i>
                                            Tipo Negociação: {{ filters().negotiationType.length }} selecionado(s)
                                        </span>
                                    }
                                    @if (filters().excludeRescindedTransacaoRepactuacaoUpTo26Overdues) {
                                        <span class="badge fs-6 fw-normal bg-danger-subtle text-danger-emphasis">
                                            <i class="ti ti-ban me-1"></i>
                                            Excluir rescindidas até 26 atrasos
                                        </span>
                                    }
                                </div>
                            </div>
                        }
                    } @else {
                        <p class="text-muted mb-0">
                            <i class="ti ti-alert-circle me-2"></i>
                            Nenhum filtro foi aplicado. A campanha incluirá todas as empresas da base.
                        </p>
                    }
                </div>
            </div>
        </div>
    `,
    styles: [`
        .step-content {
            /* O padding agora é controlado pelo container pai */
        }
    `]
})
export class Step1BasicInfoComponent implements OnInit {
    private fb = inject(FormBuilder);

    protected PersonTypeHelper = PersonTypeHelper;

    filters = input.required<any>();
    campaignData = input.required<any>();
    dataChange = output<any>();
    validityChange = output<boolean>();

    protected form!: FormGroup;

    ngOnInit(): void {
        this.form = this.createForm();

        const data = this.campaignData();
        this.form.patchValue({
            name: data.name || '',
            description: data.description || ''
        });

        // Emitir dados e validade sempre que o formulário mudar
        this.form.valueChanges.subscribe(() => {
            this.emitChange();
        });

        // Emitir validade inicial
        this.validityChange.emit(this.form.valid);
    }

    private createForm(): FormGroup {
        return this.fb.group({
            name: ['', [Validators.required, Validators.minLength(3)]],
            description: ['']
        });
    }

    private emitChange(): void {
        this.dataChange.emit(this.form.value);
        this.validityChange.emit(this.form.valid);
    }

    protected hasFilters(): boolean {
        const f = this.filters();
        return !!(f.q || f.state || f.city || f.companySize?.length || f.registrationStatus || f.personType ||
                  f.minEstimatedDebt || f.maxEstimatedDebt || f.minTotalInstallmentRemaining || f.maxTotalInstallmentRemaining ||
                  f.minDebtInstallmentValue || f.maxDebtInstallmentValue || f.minInstallmentRemaining || f.maxInstallmentRemaining ||
                  f.startMonthInstallment || f.endMonthInstallment || f.negotiationType?.length ||
                  f.excludeRescindedTransacaoRepactuacaoUpTo26Overdues);
    }
}
